/**
 * Turns the technical failures an HTTP client sees — an RFC 9457 problem body, or a
 * `fetch` that never got an answer — into sentences a customer can act on, and
 * attaches a short reference code so support can find the matching log line.
 *
 * The rewritten problem keeps the original wording under `technicalDetail` (for logs
 * and debugging) and carries `reference`. Screens keep reading `problem.detail`
 * exactly as before; it just says something readable now.
 */
import { COMPANY } from "../components/company-credit.js";
// Crockford-style alphabet: no 0/O, 1/I/L, U — easy to read out over the phone.
const REFERENCE_ALPHABET = "23456789ABCDEFGHJKMNPQRSTVWXYZ";
/** A short, speakable code like `K7Q-3XZ` — not a secret, only a lookup handle for logs. */
export function newReference() {
    const bytes = new Uint8Array(6);
    globalThis.crypto.getRandomValues(bytes);
    const chars = Array.from(bytes, (b) => REFERENCE_ALPHABET[b % REFERENCE_ALPHABET.length]);
    return `${chars.slice(0, 3).join("")}-${chars.slice(3).join("")}`;
}
/** `startsAt` / `organization_name` → "Starts at" / "Organization name". */
function fieldLabel(path) {
    const last = path && path.length > 0 ? String(path[path.length - 1]) : "";
    if (!last || /^\d+$/.test(last))
        return "One of the fields";
    const spaced = last
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[_-]+/g, " ")
        .toLowerCase();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
function issueSentence(issue) {
    const field = fieldLabel(issue.path);
    if (issue.code === "too_small" && issue.type === "string") {
        return issue.minimum === 1
            ? `${field} is required.`
            : `${field} must be at least ${issue.minimum} characters.`;
    }
    if (issue.code === "too_big" && issue.type === "string") {
        return `${field} must be at most ${issue.maximum} characters.`;
    }
    if (issue.code === "invalid_string" && issue.validation === "email") {
        return `${field} isn't a valid email address.`;
    }
    if (issue.code === "invalid_type")
        return `${field} is required.`;
    return `${field} isn't valid.`;
}
/** Zod field errors → "Password must be at least 10 characters." (first two, then "and more"). */
function validationMessage(problem) {
    const issues = (problem["issues"] ??
        problem["extensions"]?.issues);
    if (!Array.isArray(issues) || issues.length === 0)
        return null;
    const sentences = issues.slice(0, 2).map(issueSentence);
    return issues.length > 2
        ? `${sentences.join(" ")} Please check the other fields too.`
        : sentences.join(" ");
}
/** A raw `field.path: message` string — schema output that no customer should read. */
function looksLikeSchemaText(detail) {
    return /^[\w.()[\]]+: /.test(detail);
}
function withReference(message, reference) {
    return `${message} (Ref: ${reference})`;
}
/** For a fault the person can't fix: say who to tell, and quote the code. */
function withSupport(message, reference) {
    return withReference(`${message} If it keeps happening, email ${COMPANY.supportEmail}.`, reference);
}
/**
 * Rewrites a server problem for display. Business-rule messages the server wrote by hand
 * ("This license has 5 of 5 seats in use.") are kept; schema text, framework titles and
 * server faults are replaced. `reference` from the server wins over a generated one so
 * the code on screen matches the server's own log line.
 */
export function friendlyProblem(problem) {
    const serverReference = typeof problem["reference"] === "string" ? problem["reference"] : null;
    const reference = serverReference ?? newReference();
    const status = problem.status;
    const original = problem.detail;
    let message;
    if (status === 429) {
        message = "Too many attempts. Please wait a minute and try again.";
    }
    else if (status === 502 || status === 503 || status === 504) {
        message = withSupport("StagVista is temporarily unavailable. Please try again in a moment.", reference);
    }
    else if (status >= 500) {
        message = withSupport("Something went wrong on our side. Please try again.", reference);
    }
    else if (status === 413) {
        message = "That is too large to send. Try something smaller.";
    }
    else if (status === 400 || status === 422) {
        message =
            validationMessage(problem) ??
                (original && !looksLikeSchemaText(original)
                    ? original
                    : "Some of the details aren't valid. Please check them and try again.");
    }
    else if (status === 401) {
        message = original ?? "Your session has expired. Please sign in again.";
    }
    else if (status === 403) {
        message = original ?? "You don't have permission to do that.";
    }
    else if (status === 404) {
        message = original ?? "We couldn't find that.";
    }
    else if (!original) {
        message = withSupport("That didn't work. Please try again.", reference);
    }
    return {
        ...problem,
        detail: message ?? original,
        technicalDetail: original,
        reference,
    };
}
/** The problem to throw when `fetch` rejects — the request never got an answer. */
export function networkProblem(cause, context) {
    const reference = newReference();
    const name = cause instanceof Error ? cause.name : "";
    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    let message;
    if (context.target === "local") {
        message = "StagVista isn't responding. Try restarting the app.";
    }
    else if (offline) {
        message = "You appear to be offline. Check your internet connection and try again.";
    }
    else if (name === "TimeoutError" || name === "AbortError") {
        message = "This is taking longer than usual. Please try again.";
    }
    else {
        message = "Can't reach StagVista right now. Please try again in a moment.";
    }
    return {
        type: "about:blank#network",
        title: "Network error",
        status: 0,
        detail: offline && context.target === "cloud" ? message : withSupport(message, reference),
        technicalDetail: cause instanceof Error ? `${cause.name}: ${cause.message}` : String(cause),
        reference,
        instance: context.url,
        method: context.method,
        offline,
    };
}
/**
 * Records what really happened — the raw text a customer never sees — next to the
 * reference code they were shown. Goes to the console (the desktop webview's devtools /
 * a browser's console); deliberately never includes request bodies or tokens.
 */
export function logClientError(problem, context) {
    // 4xx the person can fix themselves (wrong password, missing field) is normal traffic.
    if (problem.status >= 400 && problem.status < 500 && problem.status !== 429)
        return;
    const entry = {
        reference: String(problem["reference"] ?? ""),
        target: context.target,
        method: context.method,
        url: context.url.split("?")[0],
        status: problem.status,
        message: problem.detail ?? problem.title,
        technicalDetail: String(problem["technicalDetail"] ?? ""),
        at: new Date().toISOString(),
    };
    recent.unshift(entry);
    if (recent.length > RECENT_LIMIT)
        recent.length = RECENT_LIMIT;
    console.error("[stagvista] request failed", entry);
}
const RECENT_LIMIT = 20;
const recent = [];
/** The last few faults this window has seen (newest first) — shown in Settings → About so a customer can quote them. */
export function getRecentClientErrors() {
    return recent.slice();
}
/** Test/support hook: forget everything recorded so far. */
export function clearRecentClientErrors() {
    recent.length = 0;
}
//# sourceMappingURL=friendly-error.js.map